import React from 'react';
import PropTypes from 'prop-types';
import { Creatable } from 'react-select';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { actions, Form, Control } from 'react-redux-form';

import ControlGroup from 'components/forms/ControlGroup';
import RRFValidationErrors from 'components/forms/RRFValidationErrors';
import FlashRoot from 'components/FlashMessage/FlashRoot';
import FontAwesome from 'utils/FA';
import ModalRoot from 'components/ModalRoot';
import PageHeader from 'components/PageHeader';

import { duckify, withForm } from 'utils/duckify';
import {
  customerOauthConfig as customerOauthConfigDuck,
  customerOauthConfigForm as customerOauthConfigFormDuck,
  flashMessages,
  modals
} from 'ducks';
import OAuthLogoutWarning from './OAuthLogoutWarning';

import './styles/OAuthConfig.scss';

const OAuthConfig = ({
  customerOauthConfigForm,
  duckMeta,
  resetValidity,
  showConfirmation,
  submitForm
}) => {
  if (!duckMeta.customerOauthConfig.ready) {
    return false;
  }

  const requiredTooltip = (
    <span>
      Oauth Provider
      <OverlayTrigger
        overlay={(
          <Tooltip>
            If enabled, admins will only be able to authenticate using their selected OAuth credentials.
          </Tooltip>
        )}
      >
        <FontAwesome name='info-circle' style={{ marginLeft: '5px' }} />
      </OverlayTrigger>
    </span>
  );

  const domainsTooltip = (
    <span>
      Allowed domains
      <OverlayTrigger
        overlay={(
          <Tooltip>
            Domains from which OAuth authentication is allowed.
          </Tooltip>
        )}
      >
        <FontAwesome name='info-circle' style={{ marginLeft: '5px' }} />
      </OverlayTrigger>
    </span>
  );

  const {
    form: { $form: { loadedValue: { oauth_provider: loadedValue } = {} } },
    model: { oauth_provider: currentValue }
  } = customerOauthConfigForm;

  const onSubmit = currentValue !== loadedValue
    ? showConfirmation
    : submitForm;

  return (
    <React.Fragment>
      <FlashRoot />
      <ModalRoot modals={{ OAuthLogoutWarning }} />
      <PageHeader header='OAuth Configuration' />

      <RRFValidationErrors
        formObject={customerOauthConfigForm}
        fieldNames={{
          oauth2_admin_domains: 'Allowed domains'
        }}
      />

      <Form model='customerOauthConfigForm.model' className='form-horizontal' onSubmit={onSubmit}>
        <ControlGroup  className="oauth-align-label" label={requiredTooltip} showErrors>
          <Control.select model='.oauth_provider' style={{width: '110px'}}>
            <option value={''}></option>
            <option value={'google'}>Google</option>
            <option value={'microsoft'}>Microsoft</option>
          </Control.select>
        </ControlGroup>

        <ControlGroup className="oauth-align-label" label={domainsTooltip} showErrors>
          <Control.select
            className='oauth-config-domains'
            multi
            component={Creatable}
            model='.oauth2_admin_domains'
            placeholder='Begin entering a domain name'
            mapProps={{
              onChange: ({ onChange }) => (selected) => onChange((selected || []).map((s) => s.value)),
              value: ({ modelValue }) => (modelValue || []).map((mv) => ({ value: mv, label: mv }))
            }}
          />
        </ControlGroup>

        <Control.button
          model='customerOauthConfigForm.model'
          disabled={customerOauthConfigForm.saving}
          className='btn btn-primary'
          onClick={resetValidity}
          type='submit'
        >
          Submit
        </Control.button>
      </Form>
    </React.Fragment>
  );
};

const submitForm = (dispatch) => (values) => dispatch(customerOauthConfigFormDuck.actions.save(values))
  .then(() => dispatch(flashMessages.actions.show({ message: 'OAuth Config updated.' })))
  .catch((e) => {
    if (e.hasOwnProperty('errors')) {
      Object
        .keys(e.errors)
        .map((k) => dispatch(actions.setErrors(`customerOauthConfigForm.model.${k}`, e.errors[k])));
    }

    throw (e);
  });

const mapDucksToProps = () => ({
  customerOauthConfig: customerOauthConfigDuck,
  customerOauthConfigForm: withForm(customerOauthConfigDuck, customerOauthConfigFormDuck)
});

const mapDispatchToProps = (dispatch) => ({
  resetValidity: () => dispatch(actions.resetValidity('customerOauthConfigForm.model')),
  showConfirmation: (values) => dispatch(modals.actions.show('OAuthLogoutWarning', {
    closeModal: () => dispatch(modals.actions.hide()),
    submitForm: () => submitForm(dispatch)(values),
    values
  })),
  submitForm: submitForm(dispatch)
});

OAuthConfig.propTypes = {
  customerOauthConfig: PropTypes.shape({
    _id: PropTypes.string,
    created_at: PropTypes.string,
    customer_id: PropTypes.string,
    oauth_provider: PropTypes.string,
    oauth2_admin_domains: PropTypes.arrayOf(PropTypes.string),
    updated_at: PropTypes.string
  }),
  customerOauthConfigForm: PropTypes.shape({
    form: PropTypes.shape({
      $form: PropTypes.shape({
        loadedValue: PropTypes.shape({
          oauth_provider: PropTypes.string
        })
      })
    }),
    model: PropTypes.shape({
      _id: PropTypes.string
    })
  }),
  duckMeta: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      error: PropTypes.bool,
      fetching: PropTypes.bool,
      pending: PropTypes.bool,
      ready: PropTypes.bool
    })
  }),
  resetValidity: PropTypes.func,
  showConfirmation: PropTypes.func,
  submitForm: PropTypes.func
};

OAuthConfig.defaultProps = {
  customerOauthConfig: {},
  customerOauthConfigForm: {
    form: { $form: { loadedValue: { oauth_provider: null } } }
  },
  duckMeta: {
    customerOauthConfig: {
      ready: false
    }
  },
  resetValidity: () => false,
  showConfirmation: () => false,
  submitForm: () => false
};

export default duckify(mapDucksToProps, () => ({}), mapDispatchToProps)(OAuthConfig);
